import type { DiffState } from '@com/utils/history-recorder'
import { HistoryState, useIvHistory } from './state-history'
import type { MaybePromise } from '@types-vue/shared'
import store from '@ps/store'
import { isIvPage } from '@ps/router/config'
import { validateIvData } from '@ps/store/interactive-video/common/utils'
import router from '@ps/router'
const ACTIONS_TO_KEEP = 50
const status = {
  canUndo: false,
  canRedo: false,
}

// export type ActionValue = {
//   newValue: Record<string, any>;
//   oldValue: Record<string, any>;
// };
// ActionValue 先使用 any，暂时不知道怎么写
export type ActionValue = {
  newValue: Record<any, any>
  oldValue: Record<any, any>
  stateDiff?: DiffState<HistoryState>
}

export type ActionResponse = any

export type ActionExecute = (value: ActionValue, dependAction?: any) => any
export type ActionUndo = (value: ActionValue, res: ActionResponse) => any

export type ActionParams = {
  value: ActionValue
  execute: ActionExecute
  undo: (value: ActionValue, response: ActionResponse) => any
  response?: ActionResponse
  async?: boolean // 是否异步
  methodName?: string
}

const ivHistory = useIvHistory()
const _execAction = <T extends MaybePromise<any>>(
  result: T,
  callback: () => void,
): T => {
  result instanceof Promise ? result.finally(callback) : callback()

  return result
}

const actionTracker: {
  list: ActionParams[]
  _pointer: number
  index: number
  add: (action: ActionParams) => ActionResponse
  undo: () => any
  redo: () => ActionResponse
} = {
  list: [],
  _pointer: -1,

  get index() {
    return this._pointer
  },
  set index(val) {
    this._pointer = val
    status.canUndo = this.index >= 0
    status.canRedo = this.index < this.list.length - 1
  },

  add(action: ActionParams) {
    this.list.splice(this.index + 1)
    // 存储 action，执行完动作后会将正向操作返回值更新 response 属性中
    this.list.push(action)
    this.index++
    if (this.index >= ACTIONS_TO_KEEP) {
      this.list.shift()
      this.index--
    }
    if (isIvPage()) {
      // 进入场景后有编辑过，用于互动视频的场景同步
      const currentSceneNode =
        store.state.workspace.sceneManager.currentSceneNode
      if (currentSceneNode) {
        currentSceneNode.hasEditThisTime = true
      }
    }
    // TODO：同步 -> catch throw error, and revert changed state.
    // TODO：异步 -> reject的处理
    return _execAction(action.execute(action.value), () => {
      if (isIvPage()) {
        action.value.stateDiff = ivHistory.push()
      }
      if (isIvPage() && Number(router.currentRoute.query.debug) === 1) {
        setTimeout(() => {
          validateIvData()
        }, 2000)
      }
    })
  },

  undo() {
    const action = this.list[this.index]
    this.index--

    return _execAction(
      action.undo(action.value, action.response as ActionResponse),
      () => {
        if (isIvPage() && action?.value.stateDiff) {
          ivHistory.undo()
        }
        if (isIvPage() && Number(router.currentRoute.query.debug) === 1) {
          setTimeout(() => {
            validateIvData()
          }, 2000)
        }
      },
    )
  },

  redo() {
    const action = this.list[this.index + 1]
    this.index++

    return _execAction(action.execute(action.value), () => {
      if (isIvPage() && action?.value.stateDiff) {
        ivHistory.redo()
      }
      if (isIvPage() && Number(router.currentRoute.query.debug) === 1) {
        setTimeout(() => {
          validateIvData()
        }, 2000)
      }
    })
  },
}

export default {
  status,

  get canUndo(): boolean {
    return status.canUndo
  },
  get canRedo(): boolean {
    return status.canRedo
  },

  get list(): ActionParams[] {
    return actionTracker.list
  },

  get index(): number {
    return actionTracker.index
  },

  execute({
    execute,
    undo,
    value,
    async,
    methodName,
  }: {
    execute: ActionExecute
    undo: ActionUndo
    value: ActionValue
    async: boolean
    methodName: string
  }): ActionResponse {
    return actionTracker.add({ execute, undo, value, async, methodName })
  },

  undo() {
    if (status.canUndo) return actionTracker.undo()
  },

  redo() {
    if (status.canRedo) return actionTracker.redo()
  },

  empty(): void {
    actionTracker.list = []
    actionTracker.index = -1
  },

  /**
   * 更新指定动作返回值
   * @param index 要更新的 action 的索引
   * @param response
   */
  updateTargetActionResponse(index: number, response: ActionResponse): void {
    actionTracker.list[index].response = response
  },
}
